import { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "../Button/Button";
import computerError from "../../assets/computer-fix.png";

type Props = {
    error: unknown;
    fallbackMessage?: string;
    compact?: boolean;
    onRetry?: () => void;
};

function getErrorMessage(error: unknown): string | null {
    if (error instanceof Error) {
        return error.message;
    }

    return typeof error === "string" ? error : null;
}

export function ErrorState({
    error,
    fallbackMessage = "Une erreur inattendue est survenue.",
    compact = false,
    onRetry,
}: Props) {
    const [showError, setShowError] = useState(false);
    const errorMessage = getErrorMessage(error);

    return (
        <section
            role="alert"
            className={[
                "flex w-full flex-col items-center gap-4 rounded-2xl border border-destructive/20 bg-destructive/5 p-5 text-center",
                compact ? "sm:flex-row sm:text-left" : "mx-auto max-w-3xl p-6 sm:p-8",
            ].join(" ")}
        >
            {compact ? (
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                    <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                </span>
            ) : (
                <img
                    src={computerError}
                    alt=""
                    width="1195"
                    height="896"
                    className="w-44 rounded-2xl sm:w-56"
                />
            )}

            <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-foreground">
                    Le service est momentanément indisponible
                </h2>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    {fallbackMessage}
                </p>

                {errorMessage && (
                    <div className="mt-3">
                        <button
                            type="button"
                            aria-expanded={showError}
                            onClick={() => setShowError((visible) => !visible)}
                            className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            {showError
                                ? "Masquer les détails"
                                : "Afficher les détails"}
                        </button>
                        {showError && (
                            <p className="mt-2 wrap-break-word rounded-lg bg-card p-3 text-left text-xs text-muted-foreground">
                                {errorMessage}
                            </p>
                        )}
                    </div>
                )}
            </div>

            {onRetry && (
                <Button
                    variant="tonal"
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={onRetry}
                    className="shrink-0"
                >
                    Réessayer
                </Button>
            )}
        </section>
    );
}
